<div class="space-y-6">
  <section hlmCard aria-labelledby="statement-title">
    <div hlmCardHeader>
      <h2 hlmCardTitle id="statement-title">Monatsabrechnung</h2>
      <p hlmCardDescription>
        Alle Gutscheinverkäufe eines Monats mit Verkaufserlös, Schenkwerk-Gebühren und dem Erlös
        nach Gebühren, der an dich ausgezahlt wird – dazu Erstattungen und Einlösungen. Gib die
        Abrechnung zusammen mit der Gebührenrechnung an deine Steuerberatung weiter.
      </p>
    </div>

    <div hlmCardContent class="space-y-4">
      <div class="flex flex-wrap items-end gap-2">
        <div class="flex flex-col gap-1.5">
          <label hlmFieldLabel for="statement-period">Monat</label>
          <hlm-native-select
            selectId="statement-period"
            [ngModel]="selectedPeriod()"
            (ngModelChange)="selectPeriod($event)">
            @for (p of periods; track p.value) {
              <option hlmNativeSelectOption [value]="p.value">{{ p.label }}</option>
            }
          </hlm-native-select>
        </div>
        <button
          hlmBtn
          variant="outline"
          [disabled]="!statement() || isRunningMonth() || busy() === 'statement-pdf'"
          [attr.aria-describedby]="isRunningMonth() ? 'statement-running-hint' : null"
          [attr.aria-busy]="busy() === 'statement-pdf'"
          (click)="downloadStatement('pdf')">
          @if (busy() === 'statement-pdf') {
            <hlm-spinner />
          } @else {
            <ng-icon name="lucideDownload" />
          }
          PDF
        </button>
        <button
          hlmBtn
          variant="outline"
          [disabled]="!statement() || isRunningMonth() || busy() === 'statement-csv'"
          [attr.aria-describedby]="isRunningMonth() ? 'statement-running-hint' : null"
          [attr.aria-busy]="busy() === 'statement-csv'"
          (click)="downloadStatement('csv')">
          @if (busy() === 'statement-csv') {
            <hlm-spinner />
          } @else {
            <ng-icon name="lucideDownload" />
          }
          CSV (Excel)
        </button>
      </div>
      @if (isRunningMonth()) {
        <p hlmMuted id="statement-running-hint" class="text-xs">
          Die Abrechnung zum Herunterladen gibt es nach Monatsende – bis dahin können sich die
          Zahlen noch ändern.
        </p>
      }

      @if (downloadError()) {
        <div hlmAlert variant="destructive">
          <ng-icon name="lucideTriangleAlert" />
          <p hlmAlertTitle>{{ downloadError() }}</p>
        </div>
      }

      @if (loading()) {
        <div
          class="grid gap-3 sm:grid-cols-4"
          aria-busy="true"
          aria-label="Abrechnung wird geladen">
          @for (i of [1, 2, 3, 4]; track i) {
            <div class="bg-muted h-[76px] animate-pulse rounded-md"></div>
          }
        </div>
      } @else if (error()) {
        <div hlmAlert variant="destructive">
          <ng-icon name="lucideTriangleAlert" />
          <p hlmAlertTitle>{{ error() }}</p>
          <div hlmAlertAction>
            <button hlmBtn variant="outline" size="sm" (click)="load()">
              <ng-icon name="lucideRotateCw" />
              Erneut versuchen
            </button>
          </div>
        </div>
      } @else if (statement(); as s) {
        @if (!s.is_complete) {
          <div hlmAlert>
            <ng-icon name="lucideInfo" />
            <p hlmAlertTitle>Vorläufig</p>
            <p hlmAlertDescription>Der Monat läuft noch, weitere Verkäufe können hinzukommen.</p>
          </div>
        }

        <div class="grid gap-3 sm:grid-cols-4">
          <div class="rounded-md border p-3">
            <p hlmMuted class="text-xs">Verkaufte Gutscheine</p>
            <p class="text-lg font-semibold tabular-nums">{{ s.totals.count }}</p>
            <p hlmMuted class="text-xs tabular-nums">
              Gutscheinwert {{ money(s.totals.face_value_cents) }}
            </p>
          </div>
          <div class="rounded-md border p-3">
            <p hlmMuted class="text-xs">Verkaufserlös</p>
            <p class="text-lg font-semibold tabular-nums">{{ money(s.totals.paid_cents) }}</p>
          </div>
          <div class="rounded-md border p-3">
            <p hlmMuted class="text-xs">Schenkwerk-Gebühren</p>
            <p class="text-lg font-semibold tabular-nums">{{ money(s.totals.total_fee_cents) }}</p>
          </div>
          <div class="rounded-md border p-3">
            <p hlmMuted class="text-xs">Erlös nach Gebühren</p>
            <p class="text-lg font-semibold tabular-nums">{{ money(s.totals.payout_cents) }}</p>
            <p hlmMuted class="text-xs">aus diesen Verkäufen</p>
          </div>
        </div>

        @if (s.totals.count === 0) {
          <p hlmMuted class="text-sm">
            In {{ s.period_label }} wurden keine Gutscheine verkauft. Die Abrechnung enthält
            trotzdem die Einlösungen und Erstattungen des Monats.
          </p>
        }

        @if (s.payouts.length) {
          <p class="text-sm">
            In {{ s.period_label }} hat Stripe {{ s.payouts.length }}
            {{ s.payouts.length === 1 ? 'Auszahlung' : 'Auszahlungen' }} über
            {{ money(s.payouts_total_cents) }} auf dein Bankkonto überwiesen. Das muss sich nicht
            mit dem Erlös nach Gebühren decken: Stripe zahlt Verkäufe einige Tage später aus, Anfang
            des Monats kommen also Verkäufe aus dem Vormonat, Verkäufe vom Monatsende erst im
            nächsten Monat. Welche Verkäufe eine Auszahlung enthält, zeigen die
            <a
              class="underline underline-offset-4"
              [routerLink]="['/settings']"
              [queryParams]="{ tab: 'payout-statements' }"
              >Auszahlungsbelege</a
            >.
          </p>
        }

        <p hlmMuted class="text-xs">
          @if (s.fee_invoice_number) {
            Die Gebühren sind mit der Rechnung {{ s.fee_invoice_number }} abgerechnet und wurden bei
            der Auszahlung bereits einbehalten.
            <a
              class="underline underline-offset-4"
              [routerLink]="['/settings']"
              [queryParams]="{ tab: 'invoice' }"
              >Zur Gebührenrechnung</a
            >.
          }
          {{ s.vat_note }}
        </p>
      }
    </div>
  </section>
</div>
